/**
 * Users API helpers.
 *
 * POST   /api/v1/users
 * GET    /api/v1/users/{user_id}
 * PATCH  /api/v1/users/{user_id}
 */
import axios from 'axios';
import { getUsersApiUrl } from './apiConfig';
import { http } from './http';

export type BackendUser = {
  id: string;
  username: string;
  email: string | null;
  fullName: string | null;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type CreateUserPayload = {
  username: string;
  password: string;
  email?: string | null;
  fullName?: string | null;
};

export type UpdateUserPayload = {
  email?: string | null;
  fullName?: string | null;
  password?: string | null;
  isActive?: boolean | null;
};

const DEFAULT_TIMEOUT_MS = 30000;

function readTimeoutMs(): number {
  const raw = import.meta.env.VITE_LOGIN_TIMEOUT_MS;
  if (typeof raw !== 'string') return DEFAULT_TIMEOUT_MS;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_TIMEOUT_MS;
}

function asOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message)) {
      return 'The request timed out. Please try again.';
    }
    if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
      return 'Cannot reach the users API. Check the backend / network.';
    }

    const data = err.response?.data as unknown;
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0] as { msg?: unknown };
        if (typeof first?.msg === 'string' && first.msg.trim()) return first.msg;
      }
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (err.response?.status === 401) return 'Authentication required.';
    if (err.response?.status === 403) return 'Access denied.';
    if (err.response?.status === 404) return 'User not found.';
    if (err.response?.status === 409) return 'A user with these details already exists.';
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function asBackendUser(payload: unknown): BackendUser | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const id =
    (typeof record.id === 'string' && record.id) ||
    (typeof record.user_id === 'string' && record.user_id) ||
    null;
  const username = typeof record.username === 'string' ? record.username.trim() : '';
  if (!id || !username) return null;

  return {
    id,
    username,
    email: asOptionalString(record.email),
    fullName: asOptionalString(record.fullName ?? record.full_name),
    isActive:
      typeof record.isActive === 'boolean'
        ? record.isActive
        : typeof record.is_active === 'boolean'
          ? record.is_active
          : true,
    createdAt: asOptionalString(record.createdAt ?? record.created_at),
    updatedAt: asOptionalString(record.updatedAt ?? record.updated_at),
  };
}

/**
 * Creates a new user.
 * POST /api/v1/users
 */
export async function createUser(
  payload: CreateUserPayload,
  options?: { url?: string; timeoutMs?: number }
): Promise<BackendUser> {
  const username = payload.username.trim();
  const password = payload.password;
  if (!username || !password) {
    throw new Error('Username and password are required.');
  }

  const url = options?.url || getUsersApiUrl();
  const body: Record<string, unknown> = {
    username,
    password,
  };
  const email = payload.email?.trim();
  const fullName = payload.fullName?.trim();
  if (email) body.email = email;
  if (fullName) body.fullName = fullName;

  try {
    const res = await axios.post(url, body, {
      headers: {
        'Content-Type': 'application/json',
        // Registration must not send a stale Bearer token.
        Authorization: undefined,
      },
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete (headers as Record<string, unknown>).Authorization;
          }
          return JSON.stringify(data);
        },
      ],
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    const user = asBackendUser(res.data);
    if (!user) {
      throw new Error('Create user response is missing required fields.');
    }
    return user;
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to create user. Please try again.'), {
      cause: err,
    });
  }
}

/**
 * Fetches a user by id.
 * GET /api/v1/users/{user_id}
 */
export async function fetchUser(
  userId: string,
  options?: { baseUrl?: string; timeoutMs?: number }
): Promise<BackendUser> {
  const id = userId.trim();
  if (!id) throw new Error('userId is required.');

  const baseUrl = (options?.baseUrl || getUsersApiUrl()).replace(/\/$/, '');
  const url = `${baseUrl}/${encodeURIComponent(id)}`;

  try {
    const res = await http.get(url, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    const user = asBackendUser(res.data);
    if (!user) {
      throw new Error('Get user response is missing required fields.');
    }
    return user;
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to load user details.'), { cause: err });
  }
}

/**
 * Updates a user by id.
 * PATCH /api/v1/users/{user_id}
 */
export async function updateUser(
  userId: string,
  payload: UpdateUserPayload,
  options?: { baseUrl?: string; timeoutMs?: number }
): Promise<BackendUser> {
  const id = userId.trim();
  if (!id) throw new Error('userId is required.');

  const body: Record<string, unknown> = {};
  if (payload.email !== undefined) {
    body.email = typeof payload.email === 'string' ? payload.email.trim() || null : payload.email;
  }
  if (payload.fullName !== undefined) {
    body.fullName =
      typeof payload.fullName === 'string' ? payload.fullName.trim() || null : payload.fullName;
  }
  if (payload.password !== undefined) {
    const password = typeof payload.password === 'string' ? payload.password : null;
    if (password) body.password = password;
  }
  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }

  if (Object.keys(body).length === 0) {
    throw new Error('No profile changes to save.');
  }

  const baseUrl = (options?.baseUrl || getUsersApiUrl()).replace(/\/$/, '');
  const url = `${baseUrl}/${encodeURIComponent(id)}`;

  try {
    const res = await http.patch(url, body, {
      timeout: options?.timeoutMs ?? readTimeoutMs(),
    });
    const user = asBackendUser(res.data);
    if (!user) {
      throw new Error('Update user response is missing required fields.');
    }
    return user;
  } catch (err) {
    throw new Error(toErrorMessage(err, 'Failed to update user. Please try again.'), {
      cause: err,
    });
  }
}
